import { useEffect } from 'react';
import { useCanonical } from '@/hooks/useCanonical';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Star, Sparkles, Clock, Phone, Gift } from 'lucide-react';
import { motion } from 'framer-motion';
import { formules, formulesEpilation, PLANITY_URL } from '@/data/services';
import { blogArticles } from '@/data/blog-articles';
import BlogCard from '@/components/blog/BlogCard';
import CTASection from '@/components/CTASection';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const Formules = () => {
  useCanonical('/formules');
  useEffect(() => {
    document.title = 'Nos formules avantageuses — Body by CC | Institut Beauté Sevran (93)';
    document.querySelector('meta[name="description"]')?.setAttribute('content', 'Découvrez nos formules combinées à tarifs préférentiels : soins visage, corps sculpté, beauté express et épilation. Body by CC à Sevran (93).');

    // FAQPage JSON-LD
    const existingLd = document.getElementById('formules-faqpage-jsonld');
    if (existingLd) existingLd.remove();

    const script = document.createElement('script');
    script.id = 'formules-faqpage-jsonld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        { '@type': 'Question', name: 'Comment choisir la bonne formule ?', acceptedAnswer: { '@type': 'Answer', text: 'Lors de votre premier rendez-vous, Carine réalise un diagnostic de peau complet et vous recommande la formule la plus adaptée à vos besoins et objectifs.' } },
        { '@type': 'Question', name: 'Puis-je offrir une formule en cadeau ?', acceptedAnswer: { '@type': 'Answer', text: 'Oui ! Toutes nos formules sont disponibles en carte cadeau. Rendez-vous sur notre page cadeaux ou contactez-nous directement.' } },
        { '@type': 'Question', name: 'Les formules sont-elles adaptées aux peaux noires ?', acceptedAnswer: { '@type': 'Answer', text: 'Absolument. Chaque protocole est calibré pour tous les phototypes (III à VI). Produits, actifs et paramètres sont adaptés à votre carnation.' } },
        { '@type': 'Question', name: 'Peut-on réserver une formule en ligne ?', acceptedAnswer: { '@type': 'Answer', text: 'Oui, réservez directement via Planity ou appelez-nous au 06 45 23 00 34.' } },
      ],
    });
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('formules-faqpage-jsonld');
      if (el) el.remove();
    };
  }, []);

  const advantages = [
    { icon: Star, title: 'Tarifs préférentiels', desc: 'Jusqu\'à 30% d\'économie par rapport aux soins à l\'unité.' },
    { icon: Sparkles, title: 'Résultats synergiques', desc: 'Les soins combinés multiplient les bénéfices pour votre peau.' },
    { icon: Clock, title: 'Gain de temps', desc: 'Plusieurs soins en une seule visite, optimisez votre emploi du temps.' },
    { icon: Gift, title: 'Idée cadeau', desc: 'Offrez une formule à un proche. Cartes cadeaux disponibles.' },
  ];

  const faqItems = [
    {
      q: 'Comment choisir la bonne formule ?',
      a: (
        <div className="space-y-4">
          <p>Lors de votre premier rendez-vous, Carine réalise un <strong>diagnostic de peau complet et gratuit</strong> pour déterminer la formule idéale.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl bg-primary/5 border border-primary/10 p-4 text-center">
              <Sparkles size={20} className="text-primary mx-auto mb-2" />
              <p className="font-heading font-bold text-sm text-foreground">Diagnostic</p>
              <p className="text-xs text-muted-foreground mt-1">Analyse de votre peau et objectifs</p>
            </div>
            <div className="rounded-xl bg-primary/5 border border-primary/10 p-4 text-center">
              <CheckCircle2 size={20} className="text-primary mx-auto mb-2" />
              <p className="font-heading font-bold text-sm text-foreground">Recommandation</p>
              <p className="text-xs text-muted-foreground mt-1">Formule personnalisée</p>
            </div>
            <div className="rounded-xl bg-primary/5 border border-primary/10 p-4 text-center">
              <Star size={20} className="text-primary mx-auto mb-2" />
              <p className="font-heading font-bold text-sm text-foreground">Résultats</p>
              <p className="text-xs text-muted-foreground mt-1">Visibles dès la 1ère séance</p>
            </div>
          </div>
          <p className="text-sm italic text-muted-foreground/80">Le diagnostic est offert pour tout premier rendez-vous.</p>
        </div>
      ),
    },
    {
      q: 'Puis-je offrir une formule en cadeau ?',
      a: (
        <div className="space-y-4">
          <p><strong>Oui !</strong> Toutes nos formules sont disponibles en carte cadeau — un cadeau original et mémorable.</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span>Carte cadeau <strong>PDF avec code unique</strong> envoyée par email</span></li>
            <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span>Valable <strong>6 mois</strong> après la date d'achat</span></li>
            <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span>Utilisable sur <strong>toutes les formules</strong> visage, corps et épilation</span></li>
          </ul>
          <div className="flex items-center gap-3 rounded-xl bg-accent/10 border border-accent/20 p-3">
            <Gift size={16} className="text-accent shrink-0" />
            <p className="text-sm">Découvrez notre <Link to="/offrir" onClick={() => window.scrollTo(0, 0)} className="text-primary hover:underline font-medium">page cartes cadeaux</Link> pour offrir un moment de bien-être.</p>
          </div>
        </div>
      ),
    },
    {
      q: 'Les formules sont-elles adaptées aux peaux noires et métissées ?',
      a: (
        <div className="space-y-4">
          <p><strong>Absolument, c'est notre spécialité.</strong> Chaque protocole est calibré pour les phototypes III à VI.</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span><strong>Produits sélectionnés</strong> pour les peaux foncées et sensibles</span></li>
            <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span><strong>Paramètres machines ajustés</strong> selon votre phototype</span></li>
            <li className="flex items-start gap-2.5"><CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" /><span><strong>Prévention hyperpigmentation</strong> post-inflammatoire intégrée</span></li>
          </ul>
          <p className="text-sm text-muted-foreground">Carine Tchuente, infirmière diplômée, adapte chaque soin à votre carnation pour des résultats optimaux en toute sécurité.</p>
        </div>
      ),
    },
    {
      q: 'Peut-on réserver une formule en ligne ?',
      a: (
        <div className="space-y-4">
          <p><strong>Oui</strong>, deux options s'offrent à vous :</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <a href={PLANITY_URL} target="_blank" rel="noopener noreferrer" className="rounded-xl bg-primary/5 border border-primary/10 p-4 text-center hover:border-primary/30 transition-colors block">
              <Clock size={20} className="text-primary mx-auto mb-2" />
              <p className="font-heading font-bold text-sm text-foreground">Planity</p>
              <p className="text-xs text-muted-foreground mt-1">Réservez 24h/24 en ligne</p>
            </a>
            <a href="tel:+33645230034" className="rounded-xl bg-primary/5 border border-primary/10 p-4 text-center hover:border-primary/30 transition-colors block">
              <Phone size={20} className="text-primary mx-auto mb-2" />
              <p className="font-heading font-bold text-sm text-foreground">Téléphone</p>
              <p className="text-xs text-muted-foreground mt-1">06 45 23 00 34</p>
            </a>
          </div>
          <p className="text-sm italic text-muted-foreground/80">Nous sommes ouverts du mardi au dimanche, de 10h à 20h.</p>
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Hero */}
      <section className="px-6 lg:px-8 py-20 md:py-28 bg-petrol text-petrol-foreground relative overflow-hidden">
        <div className="absolute -right-32 -top-32 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="max-w-7xl mx-auto relative">
          <span className="text-xs font-heading font-medium text-accent uppercase tracking-[0.2em]">Offres</span>
          <h1 className="font-heading text-3xl md:text-5xl font-bold mt-4 leading-tight">Nos formules avantageuses</h1>
          <p className="mt-5 text-petrol-foreground/60 max-w-2xl text-base md:text-lg leading-relaxed font-body">
            Combinez plusieurs soins à tarifs préférentiels. Chaque formule est conçue pour <strong className="text-petrol-foreground/80">maximiser les résultats</strong> tout en optimisant votre budget.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={PLANITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary text-primary-foreground font-heading font-medium text-base hover:bg-primary/90 transition-colors"
            >
              Réserver une formule <ArrowRight size={16} />
            </a>
            <a href="tel:+33645230034" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-petrol-foreground/20 text-petrol-foreground font-heading font-medium text-base hover:bg-petrol-foreground/10 transition-colors">
              <Phone size={16} /> 06 45 23 00 34
            </a>
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section className="px-6 lg:px-8 -mt-8 relative z-10 pb-8">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {advantages.map((a, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="flex items-start gap-3 p-5 rounded-2xl border border-border bg-card shadow-lg"
            >
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <a.icon size={18} className="text-primary" />
              </div>
              <div>
                <p className="font-heading font-bold text-sm text-foreground">{a.title}</p>
                <p className="text-xs text-muted-foreground font-body mt-1 leading-relaxed">{a.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Formules visage & corps */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-heading font-medium uppercase tracking-[0.2em] text-accent mb-3">Combinaisons de soins</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Formules visage & corps</h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto font-body">
              Des associations de soins pensées pour des résultats complémentaires et optimaux.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {formules.map((f, i) => (
              <motion.div
                key={f.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="p-8 rounded-2xl border border-border bg-card hover:border-primary/30 hover:shadow-lg transition-all"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                    <Sparkles size={18} className="text-accent" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-foreground">{f.name}</h3>
                </div>
                <p className="text-sm text-muted-foreground font-body leading-relaxed">{f.description}</p>
                <div className="mt-6 flex items-baseline justify-between">
                  <span className="font-heading text-2xl font-bold text-accent">{f.price}</span>
                  <span className="text-sm text-muted-foreground font-body flex items-center gap-1.5">
                    <Clock size={14} /> {f.duration}
                  </span>
                </div>
                <a
                  href={PLANITY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center gap-2 text-primary font-heading font-medium text-base hover:underline"
                >
                  Réserver cette formule <ArrowRight size={15} />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Formules épilation */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-card">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-heading font-medium uppercase tracking-[0.2em] text-accent mb-3">Épilation</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Formules d'épilation cire & fil</h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto font-body">
              Combinez plusieurs zones pour un résultat complet à prix avantageux.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {formulesEpilation.map((f, i) => (
              <motion.div
                key={f.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="p-8 rounded-2xl border border-border bg-background hover:border-primary/30 hover:shadow-lg transition-all"
              >
                <h3 className="font-heading text-xl font-bold text-foreground">{f.name}</h3>
                <p className="text-sm text-muted-foreground mt-3 font-body leading-relaxed">{f.description}</p>
                <div className="mt-6 flex items-baseline justify-between">
                  <span className="font-heading text-2xl font-bold text-accent">{f.price}</span>
                  <span className="text-sm text-muted-foreground font-body flex items-center gap-1.5">
                    <Clock size={14} /> {f.duration}
                  </span>
                </div>
                <a
                  href={PLANITY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center gap-2 text-primary font-heading font-medium text-base hover:underline"
                >
                  Réserver cette formule <ArrowRight size={15} />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-heading font-medium uppercase tracking-[0.2em] text-accent mb-3">FAQ</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Questions fréquentes</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-2xl px-6 md:px-8 bg-card data-[state=open]:border-primary/30 data-[state=open]:shadow-sm transition-all">
                <AccordionTrigger className="font-heading text-base md:text-lg font-semibold text-foreground hover:no-underline py-5">{item.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-6 font-body">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Articles liés */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-card">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-heading font-medium uppercase tracking-[0.2em] text-accent mb-3">Blog</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Articles en lien avec nos formules</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {blogArticles
              .filter(a => ['soins-corps-minceur-remodelage-sevran', 'pressotherapie-jambes-legeres-drainage', 'lipolaser-elimination-graisses-corps', 'skincleantech-soin-visage-profond-sevran', 'epilation-laser-vs-cire-comparatif', 'peeling-phototherapie-led-sevran'].includes(a.slug))
              .slice(0, 3)
              .map((article, i) => (
                <BlogCard key={article.slug} article={article} index={i} />
              ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/blog" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-2 text-primary font-heading font-medium text-base hover:underline">
              Voir tous les articles <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Lien vers soins */}
      <section className="px-6 lg:px-8 py-12 bg-secondary/30">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-muted-foreground font-body mb-4">Vous préférez un soin à l'unité ?</p>
          <Link
            to="/services"
            onClick={() => window.scrollTo(0, 0)}
            className="inline-flex items-center gap-2 text-primary font-heading font-medium text-base md:text-lg hover:underline"
          >
            Découvrir tous nos soins <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <CTASection
        title="Besoin de conseils ?"
        subtitle="Nos expertes vous guident pour choisir la formule la plus adaptée à vos besoins. Diagnostic de peau offert."
        buttonLabel="Prendre rendez-vous"
        buttonLink={PLANITY_URL}
        isExternal
      />
    </>
  );
};

export default Formules;
